(() => {
  if (window.__puffleProductLoaded) return;
  window.__puffleProductLoaded = true;

  const parseJSON = (element, fallback = []) => {
    try {
      return JSON.parse(element?.textContent || '');
    } catch (error) {
      console.warn('PUFFLE: product data could not be read.', error);
      return fallback;
    }
  };

  const displayOptionLabel = (value) => String(value || '').replace(/\s*\(\+\s*[^)]+\)\s*/g, '').trim();

  const applyCardVariantSource = (card, source) => {
    if (!card || !source) return;

    const available = source.dataset.available === 'true';
    const button = card.querySelector('[data-add-to-cart]');
    const price = card.querySelector('[data-price-current]');
    const image = card.querySelector('.product-card__image--primary');
    const secondaryImage = card.querySelector('.product-card__image--secondary');

    if (button) {
      button.disabled = !available;
      button.textContent = available
        ? (card.dataset.labelAdd || 'Add to cart')
        : (card.dataset.labelSoldOut || 'Sold out');
    }
    if (price && source.dataset.price) price.textContent = source.dataset.price;
    if (image && source.dataset.image) {
      image.src = source.dataset.image;
      image.removeAttribute('srcset');
      if (source.dataset.imageAlt) image.alt = source.dataset.imageAlt;
      secondaryImage?.setAttribute('hidden', '');
    }
    if (source.dataset.url) {
      card.querySelectorAll('[data-card-product-link]').forEach((link) => {
        link.href = source.dataset.url;
      });
    }
  };

  const bindCartForms = (root = document) => {
    root.querySelectorAll('form[data-cart-add-form]').forEach((form) => {
      if (form.dataset.cartFormBound === 'true') return;
      form.dataset.cartFormBound = 'true';

      form.addEventListener('submit', async (event) => {
        if (!window.fetch || !window.FormData) return;
        event.preventDefault();

        const button = form.querySelector('[data-add-to-cart]');
        const opener = form.__puffleSubmitOpener || event.submitter || button;
        form.__puffleSubmitOpener = null;
        const spinner = form.querySelector('[data-loading-spinner]');
        const errorRegion = form.closest('[data-product-form-wrapper]')?.querySelector('[data-product-form-error]') || form.parentElement?.querySelector('[data-card-error]');
        const errorMessage = errorRegion?.querySelector('[data-product-form-error-message]');
        if (button?.disabled || opener?.disabled) return;

        if (errorRegion) errorRegion.hidden = true;
        if (button) {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        }
        if (opener && opener !== button) {
          opener.disabled = true;
          opener.setAttribute('aria-busy', 'true');
        }
        if (spinner) spinner.hidden = false;

        try {
          const rootUrl = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootUrl}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message || 'This item could not be added.');

          const postAddAction = form.dataset.postAddAction || 'cart';
          if (window.PuffleCart?.afterAdd) {
            await window.PuffleCart.afterAdd({
              action: postAddAction,
              cartBehavior: form.dataset.cartBehavior || 'drawer',
              item: result,
              opener
            });
          } else if (postAddAction === 'cart' && form.dataset.cartBehavior === 'drawer') {
            if (window.PuffleCart?.refresh) await window.PuffleCart.refresh(true, opener);
            else document.dispatchEvent(new CustomEvent('puffle:cart:refresh', { detail: { open: true, opener } }));
          } else {
            window.location.assign(`${rootUrl}cart`);
          }
        } catch (error) {
          if (errorRegion && errorMessage) {
            errorMessage.textContent = error.message;
            errorRegion.hidden = false;
            errorRegion.focus?.();
          } else {
            window.alert(error.message);
          }
        } finally {
          const productRoot = form.closest('puffle-product');
          const selectedCardVariant = form.querySelector('[data-card-variant]')?.selectedOptions?.[0];
          const shouldRemainDisabled = productRoot
            ? !productRoot.currentVariant?.available
            : selectedCardVariant
              ? selectedCardVariant.dataset.available !== 'true'
              : false;
          if (button) {
            button.disabled = shouldRemainDisabled;
            button.removeAttribute('aria-busy');
          }
          if (opener && opener !== button) {
            opener.disabled = shouldRemainDisabled;
            opener.removeAttribute('aria-busy');
          }
          if (spinner) spinner.hidden = true;
        }
      });
    });
  };

  const bindCardVariants = (root = document) => {
    root.querySelectorAll('[data-card-variant]').forEach((select) => {
      if (select.dataset.variantBound === 'true') return;
      select.dataset.variantBound = 'true';

      const card = select.closest('[data-product-card]');
      const applySelection = () => {
        const option = select.selectedOptions[0];
        if (!option || !card) return;

        applyCardVariantSource(card, option);
      };

      select.addEventListener('change', applySelection);
      applySelection();
    });
  };

  const bindCardSwatches = (root = document) => {
    root.querySelectorAll('[data-product-card]').forEach((card) => {
      if (card.dataset.cardSwatchesBound === 'true') return;
      const swatches = Array.from(card.querySelectorAll('[data-card-swatch]'));
      if (!swatches.length) return;
      card.dataset.cardSwatchesBound = 'true';

      swatches.forEach((swatch) => {
        swatch.addEventListener('click', (event) => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          if (!swatch.dataset.variantId) return;
          event.preventDefault();

          const variantSelect = card.querySelector('[data-card-variant]');
          const matchingOption = variantSelect
            ? Array.from(variantSelect.options).find((option) => option.value === swatch.dataset.variantId)
            : null;

          if (matchingOption) {
            variantSelect.value = matchingOption.value;
            variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            applyCardVariantSource(card, swatch);
          }

          swatches.forEach((item) => {
            const isActive = item === swatch;
            item.classList.toggle('is-active', isActive);
            if (isActive) item.setAttribute('aria-current', 'true');
            else item.removeAttribute('aria-current');
          });
        });
      });
    });
  };

  const hydrateCardSecondaryImage = (card) => {
    const image = card.querySelector('[data-card-secondary-image][data-src]');
    if (!image || image.hidden) return;

    const markReady = () => {
      image.dataset.secondaryState = 'ready';
    };

    image.dataset.secondaryState = 'loading';
    image.addEventListener('load', markReady, { once: true });
    image.addEventListener('error', () => image.remove(), { once: true });
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    image.src = image.dataset.src;
    image.removeAttribute('data-srcset');
    image.removeAttribute('data-src');

    if (image.complete && image.naturalWidth > 1) markReady();
  };

  const bindCardSecondaryImages = (root = document) => {
    root.querySelectorAll('[data-product-card]').forEach((card) => {
      if (card.dataset.secondaryImageBound === 'true') return;
      if (!card.querySelector('[data-card-secondary-image][data-src]')) return;
      card.dataset.secondaryImageBound = 'true';

      const hydrate = () => hydrateCardSecondaryImage(card);
      card.addEventListener('pointerenter', hydrate, { once: true });
      card.addEventListener('focusin', hydrate, { once: true });
    });
  };

  if (!customElements.get('puffle-product')) {
    class PuffleProduct extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.closest('[data-section-id]')?.dataset.sectionId;
        this.variants = parseJSON(this.querySelector('[data-product-variants]'));
        this.optionInputs = Array.from(this.querySelectorAll('[data-product-option]'));
        this.currentVariant = this.variantFromSelection() || this.variants.find((variant) => variant.available) || this.variants[0];

        this.optionInputs.forEach((input) => input.addEventListener('change', () => this.onOptionChange()));
        this.querySelectorAll('[data-quantity-change]').forEach((button) => {
          button.addEventListener('click', () => this.changeQuantity(Number(button.dataset.quantityChange)));
        });
        this.querySelector('.quantity__input')?.addEventListener('change', (event) => this.normalizeQuantity(event.currentTarget));

        const stickySubmit = this.querySelector('[data-sticky-submit]');
        stickySubmit?.addEventListener('click', () => {
          const form = this.querySelector('form[data-cart-add-form]');
          const mainSubmit = this.querySelector('[data-add-to-cart]');
          if (!form || !mainSubmit) return;
          form.__puffleSubmitOpener = stickySubmit;
          if (form.requestSubmit) form.requestSubmit(mainSubmit);
          else mainSubmit.click();
        });

        this.querySelectorAll('[data-share-button]').forEach((button) => {
          button.addEventListener('click', () => this.share(button));
        });

        this.setupStickyButton();
        this.updateOptionAvailability();
        if (this.currentVariant) this.applyVariant(this.currentVariant, false);
      }

      variantFromSelection() {
        if (this.variants.length <= 1 || this.optionInputs.length === 0) return this.variants[0];

        const selectedOptions = [];
        this.optionInputs.forEach((input) => {
          if (input.type === 'radio' && !input.checked) return;
          selectedOptions[Number(input.dataset.optionPosition) - 1] = input.value;
        });

        return this.variants.find((variant) =>
          variant.options.every((option, index) => option === selectedOptions[index])
        );
      }

      onOptionChange() {
        this.updateSelectedOptionLabels();
        this.updateOptionAvailability();
        const variant = this.variantFromSelection();
        this.applyVariant(variant, true);
      }

      updateOptionAvailability() {
        const selectedOptions = [];
        this.optionInputs.forEach((input) => {
          if (input.type === 'radio' && !input.checked) return;
          selectedOptions[Number(input.dataset.optionPosition) - 1] = input.value;
        });

        this.optionInputs.forEach((input) => {
          const position = Number(input.dataset.optionPosition) - 1;
          const candidateOptions = [...selectedOptions];
          candidateOptions[position] = input.value;
          const possible = this.variants.some((variant) =>
            variant.available && variant.options.every((option, index) => option === candidateOptions[index])
          );
          const label = input.id ? this.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
          label?.classList.toggle('is-unavailable', !possible);
        });
      }

      updateSelectedOptionLabels() {
        this.optionInputs.forEach((input) => {
          if (input.type === 'radio' && !input.checked) return;
          const label = this.querySelector(`[data-selected-option="${input.dataset.optionPosition}"]`);
          const selectedOption = input.selectedOptions?.[0];
          const displayValue = selectedOption?.dataset.optionLabel || input.dataset.optionLabel || displayOptionLabel(input.value);
          if (label) label.textContent = displayValue;
        });
      }

      applyVariant(variant, updateHistory) {
        this.currentVariant = variant;
        const isAvailable = Boolean(variant?.available);

        this.querySelectorAll('[data-variant-id]').forEach((input) => {
          input.name = 'id';
          input.value = variant?.id || '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        this.updatePrice(variant);
        this.updateButtons(variant, isAvailable);
        this.updateSku(variant);
        this.updateInventory(variant);
        this.updateQuantityRules(variant);

        if (updateHistory && variant?.id) {
          document.dispatchEvent(
            new CustomEvent('puffle:variant-change', {
              detail: { sectionId: this.sectionId, featuredMediaId: variant.featuredMediaId, variant }
            })
          );
        }

        if (updateHistory && variant?.id && this.dataset.updateUrl === 'true') {
          const url = new URL(this.dataset.productUrl, window.location.origin);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
        }

        document.dispatchEvent(
          new CustomEvent('puffle:product-variant-updated', {
            detail: { sectionId: this.sectionId, variant }
          })
        );
      }

      updatePrice(variant) {
        const price = this.querySelector('[data-product-price]');
        if (!price) return;

        const current = price.querySelector('[data-price-current]');
        const compare = price.querySelector('[data-price-compare]');
        const saleBadge = price.querySelector('[data-price-sale-badge]');
        const soldOutBadge = price.querySelector('[data-price-sold-out-badge]');
        const unitPrice = price.querySelector('[data-unit-price]');
        const isOnSale = Boolean(variant?.compareAtPrice && variant.compareAtPrice > variant.price);

        if (current) current.textContent = variant?.priceFormatted || '';
        if (compare) {
          compare.textContent = isOnSale ? variant.compareAtPriceFormatted : '';
          compare.hidden = !isOnSale;
        }
        if (saleBadge) saleBadge.hidden = !isOnSale;
        if (soldOutBadge) soldOutBadge.hidden = Boolean(variant?.available);
        price.querySelector('[data-price]')?.classList.toggle('price--on-sale', isOnSale);
        if (unitPrice) {
          const hasUnitPrice = Boolean(variant?.unitPriceFormatted && variant?.unitReferenceUnit);
          unitPrice.hidden = !hasUnitPrice;
          const unitPriceCurrent = unitPrice.querySelector('[data-unit-price-current]');
          const unitReference = unitPrice.querySelector('[data-unit-reference]');
          if (unitPriceCurrent) unitPriceCurrent.textContent = hasUnitPrice ? variant.unitPriceFormatted : '';
          if (unitReference) unitReference.textContent = hasUnitPrice
            ? `${variant.unitReferenceValue && variant.unitReferenceValue !== 1 ? variant.unitReferenceValue : ''}${variant.unitReferenceUnit}`
            : '';
        }
      }

      updateButtons(variant, isAvailable) {
        const addLabel = this.dataset.labelAdd || 'Add to cart';
        const soldOutLabel = this.dataset.labelSoldOut || 'Sold out';
        const unavailableLabel = this.dataset.labelUnavailable || 'Unavailable';
        const label = variant ? (isAvailable ? addLabel : soldOutLabel) : unavailableLabel;

        this.querySelectorAll('[data-add-to-cart]').forEach((button) => {
          button.disabled = !isAvailable;
          const text = button.querySelector('[data-add-to-cart-text]');
          if (text) text.textContent = label;
          const price = button.querySelector('[data-add-to-cart-price]');
          if (price) {
            price.textContent = variant?.priceFormatted || '';
            price.hidden = !isAvailable;
          }
        });

        const stickyButton = this.querySelector('[data-sticky-submit]');
        if (stickyButton) stickyButton.disabled = !isAvailable;
        const stickyText = this.querySelector('[data-sticky-add-text]');
        if (stickyText) stickyText.textContent = label;
        const stickyPrice = this.querySelector('[data-sticky-price]');
        if (stickyPrice) stickyPrice.textContent = variant?.priceFormatted || '';
        const stickyVariant = this.querySelector('[data-sticky-variant]');
        if (stickyVariant) stickyVariant.textContent = variant?.title === 'Default Title' ? '' : displayOptionLabel(variant?.title);
        const stickyImage = this.querySelector('[data-sticky-image]');
        if (stickyImage && variant?.featuredMediaPreview) stickyImage.src = variant.featuredMediaPreview;

        if (!isAvailable && this.stickyBar) this.stickyBar.hidden = true;
      }

      updateSku(variant) {
        const sku = this.querySelector('[data-product-sku]');
        if (!sku) return;
        sku.hidden = !variant?.sku;
        const value = sku.querySelector('[data-sku-value]');
        if (value) value.textContent = variant?.sku || '';
      }

      updateInventory(variant) {
        const inventory = this.querySelector('[data-product-inventory]');
        if (!inventory) return;

        const threshold = Number(inventory.dataset.lowInventoryThreshold || 5);
        const showExact = inventory.dataset.showExact === 'true';
        let message = '';

        if (!variant) {
          message = this.dataset.labelUnavailable || 'Unavailable';
        } else if (variant.inventoryManagement) {
          if (variant.inventoryQuantity > 0) {
            message = showExact && variant.inventoryQuantity <= threshold
              ? `Only ${variant.inventoryQuantity} left`
              : 'In stock';
          } else if (variant.inventoryPolicy === 'continue') {
            message = 'Available to order';
          } else {
            message = this.dataset.labelSoldOut || 'Sold out';
          }
        } else {
          message = variant.available ? 'In stock' : (this.dataset.labelSoldOut || 'Sold out');
        }

        inventory.textContent = message;
        inventory.classList.toggle('is-sold-out', !variant?.available);
      }

      updateQuantityRules(variant) {
        const input = this.querySelector('.quantity__input');
        if (!input || !variant?.quantityRule) return;

        const minimum = Number(variant.quantityRule.min || 1);
        const increment = Number(variant.quantityRule.increment || 1);
        input.min = String(minimum);
        input.step = String(increment);
        if (variant.quantityRule.max) input.max = String(variant.quantityRule.max);
        else input.removeAttribute('max');
        this.normalizeQuantity(input);
      }

      normalizeQuantity(input) {
        if (!input) return;
        const minimum = Number(input.min || 1);
        const increment = Math.max(1, Number(input.step || 1));
        const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const rawValue = Number(input.value);
        const safeValue = Number.isFinite(rawValue) ? rawValue : minimum;
        const alignedValue = minimum + Math.floor(Math.max(0, safeValue - minimum) / increment) * increment;
        const normalizedValue = Math.min(maximum, Math.max(minimum, alignedValue));
        input.value = String(normalizedValue);

        const quantity = input.closest('.quantity');
        const minus = quantity?.querySelector('[data-quantity-change="-1"]');
        const plus = quantity?.querySelector('[data-quantity-change="1"]');
        if (minus) minus.disabled = normalizedValue <= minimum;
        if (plus) plus.disabled = Number.isFinite(maximum) && normalizedValue + increment > maximum;
      }

      changeQuantity(direction) {
        const input = this.querySelector('.quantity__input');
        if (!input) return;

        const step = Number(input.step || 1);
        const minimum = Number(input.min || 1);
        const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
        const next = Math.min(maximum, Math.max(minimum, Number(input.value || minimum) + direction * step));
        input.value = String(next);
        this.normalizeQuantity(input);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      setupStickyButton() {
        this.stickyBar = this.querySelector('[data-sticky-atc]');
        const mainButton = this.querySelector('[data-add-to-cart]');
        if (!this.stickyBar || !mainButton) return;

        this.mainAddButton = mainButton;
        this.syncStickyButton = () => {
          if (this.stickyFrame) return;
          this.stickyFrame = window.requestAnimationFrame(() => {
            this.stickyFrame = null;
            const hasScrolledPastMainButton = this.mainAddButton.getBoundingClientRect().bottom <= 0;
            this.stickyBar.hidden = !hasScrolledPastMainButton || !this.currentVariant?.available;
          });
        };

        if ('IntersectionObserver' in window) {
          this.stickyObserver = new IntersectionObserver(() => this.syncStickyButton(), { threshold: 0 });
          this.stickyObserver.observe(mainButton);
        }
        window.addEventListener('scroll', this.syncStickyButton, { passive: true });
        window.addEventListener('resize', this.syncStickyButton, { passive: true });
        this.syncStickyButton();
      }

      async share(button) {
        const url = button.dataset.shareUrl || window.location.href;
        const label = button.querySelector('[data-share-label]');
        try {
          if (navigator.share) {
            await navigator.share({ title: document.title, url });
          } else if (navigator.clipboard) {
            await navigator.clipboard.writeText(url);
            if (label) {
              const original = label.textContent;
              label.textContent = 'Link copied';
              window.setTimeout(() => { label.textContent = original; }, 1800);
            }
          } else {
            window.prompt('Copy this link', url);
          }
        } catch (error) {
          if (error?.name !== 'AbortError') console.warn('PUFFLE: sharing was not available.', error);
        }
      }

      disconnectedCallback() {
        this.stickyObserver?.disconnect();
        if (this.syncStickyButton) {
          window.removeEventListener('scroll', this.syncStickyButton);
          window.removeEventListener('resize', this.syncStickyButton);
        }
        if (this.stickyFrame) window.cancelAnimationFrame(this.stickyFrame);
      }
    }

    customElements.define('puffle-product', PuffleProduct);
  }

  if (!customElements.get('product-recommendations')) {
    class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.url || this.dataset.loaded === 'true') return;
        if (!('IntersectionObserver' in window)) {
          this.load();
          return;
        }

        this.recommendationObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.recommendationObserver?.disconnect();
          this.recommendationObserver = null;
          this.load();
        }, { rootMargin: '600px 0px' });
        this.recommendationObserver.observe(this);
      }

      disconnectedCallback() {
        this.recommendationObserver?.disconnect();
        if (this.retryTimer) window.clearTimeout(this.retryTimer);
      }

      async load() {
        if (!this.dataset.url || this.dataset.loaded === 'true') return;
        this.dataset.loaded = 'true';
        try {
          const response = await fetch(this.dataset.url, { headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error(`Recommendation request failed (${response.status})`);
          const markup = await response.text();
          const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
          const recommendations = documentFragment.querySelector('product-recommendations');

          if (recommendations?.innerHTML.trim()) {
            this.closest('.product-recommendations-section')?.removeAttribute('hidden');
            this.innerHTML = recommendations.innerHTML;
            bindCartForms(this);
            bindCardVariants(this);
            bindCardSwatches(this);
            bindCardSecondaryImages(this);
          } else {
            this.closest('.product-recommendations-section')?.setAttribute('hidden', '');
          }
        } catch (error) {
          const retryCount = Number(this.dataset.retryCount || 0);
          if (retryCount < 2 && this.isConnected) {
            this.dataset.retryCount = String(retryCount + 1);
            this.dataset.loaded = 'false';
            this.retryTimer = window.setTimeout(() => {
              this.retryTimer = null;
              if (this.isConnected) this.load();
            }, 1800 * (retryCount + 1));
          } else {
            this.closest('.product-recommendations-section')?.setAttribute('hidden', '');
          }
          console.warn('PUFFLE: product recommendations could not be loaded.', error);
        }
      }
    }

    customElements.define('product-recommendations', ProductRecommendations);
  }

  const setupProductModels = () => {
    const modelData = document.querySelector('[data-product-models]');
    if (!modelData || modelData.dataset.loaded === 'true' || !window.Shopify?.loadFeatures) return;
    modelData.dataset.loaded = 'true';

    window.Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: (error) => {
          if (error || !window.ShopifyXR) return;
          window.ShopifyXR.addModels(parseJSON(modelData));
          window.ShopifyXR.setupXRElements();
        }
      },
      { name: 'model-viewer-ui', version: '1.0', onLoad: () => {} }
    ]);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setupProductModels);
  else setupProductModels();

  bindCartForms();
  bindCardVariants();
  bindCardSwatches();
  bindCardSecondaryImages();
  document.addEventListener('shopify:section:load', (event) => {
    bindCartForms(event.target);
    bindCardVariants(event.target);
    bindCardSwatches(event.target);
    bindCardSecondaryImages(event.target);
  });
})();
